/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { useState, useEffect } from 'react'
import type { PeerConfig } from '@/lib/storage'

interface UsePeerManagementReturn {
  peerHeights: Record<string, number>
  resizingPeer: string | null
  expandedPeer: string | null
  draggedPeer: string | null
  editingPeerId: string | null
  setPeerHeights: React.Dispatch<React.SetStateAction<Record<string, number>>>
  setResizingPeer: (peerId: string | null) => void
  setExpandedPeer: (peerId: string | null) => void
  setDraggedPeer: (peerId: string | null) => void
  setEditingPeerId: (peerId: string | null) => void
  handlePeerResize: (e: MouseEvent) => void
  handlePeerResizeEnd: () => void
}

export function usePeerManagement(peers: PeerConfig[]): UsePeerManagementReturn {
  const [peerHeights, setPeerHeights] = useState<Record<string, number>>({})
  const [resizingPeer, setResizingPeer] = useState<string | null>(null)
  const [expandedPeer, setExpandedPeer] = useState<string | null>(null)
  const [draggedPeer, setDraggedPeer] = useState<string | null>(null)
  const [editingPeerId, setEditingPeerId] = useState<string | null>(null)

  // Initialize peer heights when peers change
  useEffect(() => {
    if (peers.length > 0) {
      setPeerHeights((prev) => {
        const newHeights: Record<string, number> = {}
        peers.forEach((peer) => {
          newHeights[peer.id] = prev[peer.id] || 200 // Default height 200px
        })
        // Keep judge height
        newHeights['judge'] = prev['judge'] || 200
        return newHeights
      })
    }
  }, [peers.length])

  // Handle peer panel resizing
  const handlePeerResize = (e: MouseEvent) => {
    if (!resizingPeer) return

    const startY = e.clientY
    const startHeight = peerHeights[resizingPeer] || 200

    const onMouseMove = (moveEvent: MouseEvent) => {
      const deltaY = moveEvent.clientY - startY
      const newHeight = Math.max(100, Math.min(800, startHeight + deltaY))
      setPeerHeights((prev) => ({ ...prev, [resizingPeer]: newHeight }))
    }

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      setResizingPeer(null)
    }

    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)
  }

  const handlePeerResizeEnd = () => {
    setResizingPeer(null)
  }

  useEffect(() => {
    if (resizingPeer) {
      document.addEventListener('mousedown', handlePeerResize as any)
      return () => {
        document.removeEventListener('mousedown', handlePeerResize as any)
      }
    }
  }, [resizingPeer])

  return {
    peerHeights,
    resizingPeer,
    expandedPeer,
    draggedPeer,
    editingPeerId,
    setPeerHeights,
    setResizingPeer,
    setExpandedPeer,
    setDraggedPeer,
    setEditingPeerId,
    handlePeerResize,
    handlePeerResizeEnd,
  }
}

